import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { FieldTree, disabled, form, required } from '@angular/forms/signals';
import {
  DropdownComponent,
  DropdownOption,
} from '../dropdown/dropdown.component';
import { ExpandableTextComponent } from '../../expandable-text/expandable-text.component';
import {
  VSP_CATEGORIES,
  VspCategory,
  VspScoredItem,
  VspStandard,
} from './vsp-regulation-lookup.data';
import {
  pointsText,
  splitBreadcrumb,
  withoutNumberDash,
} from '../regulation-lookup-labels';

export interface VspRegulationLookupValue {
  categoryId: string;
  categoryName: string;
  itemNo: string;
  itemLabel: string;
  section: string | null;
  breadcrumb: string;
  title: string;
  pointValue: number;
  critical: boolean;
  description: string;
}

type PickerValue = string | null;
interface PickerField {
  id: string;
  label: string;
  placeholder: string;
  options: DropdownOption[];
}
interface ItemEntry {
  category: VspCategory;
  item: VspScoredItem;
}

export const VSP_REGULATION_LOOKUP_PLACEHOLDER =
  'VSP description and points will be shown here based on the selected item.';
export const VSP_STANDARD_PROMPT =
  'Select a specific standard for its full requirement text.';

const ITEM_ENTRIES: ItemEntry[] = VSP_CATEGORIES.flatMap(category =>
  category.items.map(item => ({ category, item }))
);

export function vspPointsBadge(value: {
  pointValue: number;
  critical: boolean;
}): string {
  return `${pointsText(value.pointValue)}${value.critical ? ' · critical' : ''}`;
}

export function vspItemOptionLabel(item: VspScoredItem): string {
  return `${withoutNumberDash(item.label)} · ${vspPointsBadge(item)}`;
}

export function vspStandardOptionLabel(standard: VspStandard): string {
  return `${withoutNumberDash(standard.label)}${standard.critical ? ' · critical' : ''}`;
}

const ITEM_OPTIONS: DropdownOption[] = ITEM_ENTRIES.map(
  ({ category, item }) => ({
    label: vspItemOptionLabel(item),
    value: item.itemNo,
    group: category.name,
  })
);

@Component({
  selector: 'app-vsp-regulation-lookup',
  imports: [DropdownComponent, ExpandableTextComponent],
  templateUrl: './vsp-regulation-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VspRegulationLookupComponent implements OnInit {
  readonly placeholderText = VSP_REGULATION_LOOKUP_PLACEHOLDER;
  readonly standardPrompt = VSP_STANDARD_PROMPT;
  readonly field = input.required<FieldTree<VspRegulationLookupValue | null>>();
  readonly index = input(0);

  protected readonly state = computed(() => this.field()());
  private readonly idSuffix = computed(() =>
    this.index() ? `-${this.index() + 1}` : ''
  );

  private readonly itemValue = signal<PickerValue>(null);
  private readonly selectedEntry = computed(
    () => ITEM_ENTRIES.find(e => e.item.itemNo === this.itemValue()) ?? null
  );
  private readonly standardValue = linkedSignal<ItemEntry | null, PickerValue>({
    source: this.selectedEntry,
    computation: () => null,
  });
  private readonly selectedStandard = computed(
    () =>
      this.selectedEntry()?.item.subItems.find(
        s => s.section === this.standardValue()
      ) ?? null
  );

  readonly itemTree = form(this.itemValue, path => {
    disabled(path, { when: () => this.state().disabled() });
    required(path, { when: () => this.state().required() });
  });
  readonly standardTree = form(this.standardValue, path =>
    disabled(path, {
      when: () => this.state().disabled() || !this.selectedEntry(),
    })
  );

  readonly itemField = computed<PickerField>(() => ({
    id: `vsp-item${this.idSuffix()}`,
    label: 'Scored item',
    placeholder: 'Select a scored item',
    options: ITEM_OPTIONS,
  }));
  readonly standardField = computed<PickerField>(() => ({
    id: `vsp-standard${this.idSuffix()}`,
    label: 'Specific standard',
    placeholder: 'Select a specific standard',
    options: (this.selectedEntry()?.item.subItems ?? []).map(s => ({
      label: vspStandardOptionLabel(s),
      value: s.section,
    })),
  }));

  private readonly pickedValue = computed<VspRegulationLookupValue | null>(
    () => {
      const entry = this.selectedEntry();
      if (!entry) return null;
      const { category, item } = entry;
      const standard = this.selectedStandard();
      const itemPath = `${category.name} / Item ${item.itemNo}`;
      const picked = {
        categoryId: category.id,
        categoryName: category.name,
        itemNo: item.itemNo,
        itemLabel: item.label,
      };
      if (!standard) {
        return {
          ...picked,
          section: null,
          breadcrumb: itemPath,
          title: item.description,
          pointValue: item.pointValue,
          critical: item.critical,
          description: item.description,
        };
      }
      return {
        ...picked,
        section: standard.section,
        breadcrumb: `${itemPath} / ${standard.section}`,
        title: standard.title,
        pointValue: standard.pointValue,
        critical: standard.critical,
        description: standard.description,
      };
    }
  );

  readonly compositeValue = linkedSignal<
    VspRegulationLookupValue | null,
    VspRegulationLookupValue | null
  >({ source: this.pickedValue, computation: value => value });

  readonly pointsBadge = vspPointsBadge;

  readonly breadcrumbParts = computed(() =>
    splitBreadcrumb(this.compositeValue()?.breadcrumb ?? '')
  );

  constructor() {
    effect(() => {
      const value = this.compositeValue();
      untracked(() => {
        const state = this.state();
        if (sameVspSelection(state.value(), value)) return;
        state.value.set(value);
        state.markAsDirty();
      });
    });
    effect(() => {
      if (this.state().touched())
        untracked(() => this.itemTree().markAsTouched());
    });
  }

  ngOnInit(): void {
    const value = this.state().value();
    if (!value) return;
    this.compositeValue.set(value);
    const entry = value.section
      ? ITEM_ENTRIES.find(e =>
          e.item.subItems.some(s => s.section === value.section)
        )
      : ITEM_ENTRIES.find(e => e.item.itemNo === value.itemNo);
    if (!entry) return;
    this.itemValue.set(entry.item.itemNo);
    this.standardValue.set(value.section);
  }
}

export function sameVspSelection(
  a: VspRegulationLookupValue | null,
  b: VspRegulationLookupValue | null
): boolean {
  return (
    (a?.itemNo ?? null) === (b?.itemNo ?? null) &&
    (a?.section ?? null) === (b?.section ?? null)
  );
}
